import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import { Text } from 'office-ui-fabric-react/lib/Text';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModule from '../../components/SubModule';
import Module from '../../components/Module';
import CardHeader from '../../components/CardHeader';
import ModuleHeader from '../../components/ModuleHeader/ModuleHeader';
import ModuleContent from '../../components/ModuleContent';
import SubModuleContent from '../../components/SubModuleContent';
import ModuleContentList from '../../components/ModuleContentList';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import Link from '../../components/Link';
import { fetchProduct } from '../../actions/products';
import { fetchPartner } from '../../actions/partners';
import DetailsList from '../../components/DetailsList';
import ProductAgreementsList from '../../components/ProductAgreementsList';
import Protected from '../../components/Protected';
import styles from './ProductDetails.style';

const columns = [
  {
    key: 'id',
    name: 'Product IDs',
    fieldName: 'id',
    minWidth: 250,
    maxWidth: 250,
    className: styles.productId,
  },
];

const ProductDetails = () => {
  const dispatch = useDispatch();
  const { tokenId } = useParams();
  const product = useSelector(state => get(state, `entities.products.${tokenId}`, {}));
  const partnerAddress = useSelector(state => get(state, `entities.products.${tokenId}.owner`, ''));
  const partnerName = useSelector(state =>
    get(state, `entities.partners.${partnerAddress}.name`, ''),
  );

  const agreements = (product.agreements || [])
    .map(
      agreement =>
        typeof agreement !== 'string' && {
          ...agreement,
          address: agreement.contract.address,
          name: agreement.contract.name,
          contractId: agreement.contract.contractId,
          agreement: agreement.name,
          link: `/contracts/${agreement.contract.address}`,
        },
    )
    .filter(Boolean);

  useEffect(() => {
    dispatch(fetchProduct(tokenId));
  }, [dispatch, tokenId]);

  useEffect(() => {
    if (partnerAddress) {
      dispatch(fetchPartner(partnerAddress));
    }
  }, [dispatch, partnerAddress]);

  return (
    <>
      <PageTitle>
        {partnerName && (
          <>
            <Link href={`/partners/${partnerAddress}`}>{partnerName}</Link>
            <span className={styles.slash}> / </span>
          </>
        )}
        {product.name}
      </PageTitle>

      <Main>
        <Module>
          <ModuleHeader
            data-testid="edit-product-details"
            extra={
              <Protected permissions="editProduct">
                <Link href={`/products/${tokenId}/edit`}>Edit Product Details</Link>
              </Protected>
            }
          >
            Product Details
          </ModuleHeader>
          <ModuleContent>
            <CardHeader variant="large" leftBorder>
              {`Token ID: #${tokenId || ''}`}
            </CardHeader>
            <SubModule>
              <SubModuleHeader id="productIdentifiers" data-testid="product-identifier-heading">
                Product Identifiers
              </SubModuleHeader>
              <SubModuleContent data-testid="product-identifier-content">
                <DetailsList
                  items={get(product, 'metadata.productIds', []).map(id => ({
                    id,
                  }))}
                  columns={columns}
                />
              </SubModuleContent>
            </SubModule>
          </ModuleContent>
        </Module>

        {agreements.length > 0 && (
          <Module>
            <ModuleHeader>Associated Royalty Fees</ModuleHeader>
            <ModuleContentList className={styles.associatedRoyalties}>
              <ProductAgreementsList items={agreements} />
            </ModuleContentList>
            <Protected permissions="createAgreement">
              <Text as="p" className={styles.productDataContent}>
                Products can be associated to agreements from the contract page.
              </Text>
            </Protected>
          </Module>
        )}
      </Main>
    </>
  );
};

export default ProductDetails;
